import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import { Button, Modal, Form, Input, Tag, Select, Dropdown } from "antd";
import { BookOutlined, DownOutlined } from "@ant-design/icons";
import { withUser } from "@unifiedpatents/sso";
import { withRouter } from "react-router";
import useSWR, { useSWRConfig } from "swr";
import { createQuery, getProjects, createProject, createPortfolioByProjectId } from "../../services/api-users";

const SaveUserQuery = ({ user, router, filter }) => {
  const userId = user && user.profile ? user.profile.user_id : null;

  const [portfolioName, setPortfolioName] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("bare"); // "new" | "existing" | "bare"

  const { data: projects = [] } = useSWR(
    userId ? ["projects", userId] : null,
    ([, id]) => getProjects(id)
  );
  const { mutate: globalMutate } = useSWRConfig();

  const isQueryValid = filter && Object.keys(filter).length > 0;
  const saveDisabled = !(userId && isQueryValid);

  const submitDisabled = {
    "new": !projectName,
    "existing": !projectId,
    "bare": !portfolioName
  }[mode] || !portfolioName;
  const onProjectChange = id => router.push(`/patents/portfolio/my/${id}`);
  const insertQuery = (filter, userId, name) => createQuery({
    query: JSON.stringify(filter),
    user_id: userId,
    name: mode === "bare" ? name : null // query attached to project doesn't need a name
  }).then(q => q[0].id);
  const modeLabel = {
    bare: "Save to my searches",
    new: "Save to a new portfolio project",
    existing: "Save to an existing portfolio project"
  };
  const modeItems = Object.keys(modeLabel).map(key => ({
    key,
    label: modeLabel[key],
    disabled: key === "existing" && (projects || []).length === 0
  }));

  return (
    <Fragment>
      <Button
        size="small"
        icon={<BookOutlined />}
        onClick={() => setVisible(true)}
        disabled={saveDisabled}
        loading={loading}
      >
        Save Search
      </Button>
      <Modal
        title="Save Current Search"
        closable={true}
        open={visible}
        onOk={() => {
          setLoading(true);
          if (mode === "bare") {
            // Save a named query without attaching it to any project
            return insertQuery(filter, userId, portfolioName)
              .then(() => globalMutate([`/users/${userId}/queries`, userId]))
              .then(() => setLoading(false))
              .then(() => setVisible(false))
              .catch(() => setLoading(false));
          }
          if (mode === "new" && projectName) {
            return createProject(userId, { name: projectName || new Date().toLocaleString() })
              .then(([{ id }]) => {
                return insertQuery(filter, userId, null)
                  .then(queryId => createPortfolioByProjectId(userId, id, {
                    name: portfolioName,
                    query_id: queryId
                  }))
                  .then(() => setLoading(false))
                  .then(() => setVisible(false))
                  .then(() => onProjectChange(id));
              })
              .catch(() => setLoading(false));
          } else if (mode !== "new" && projectId) {
            return insertQuery(filter, userId, null)
              .then(queryId => createPortfolioByProjectId(userId, projectId, {
                name: portfolioName,
                query_id: queryId
              }))
              .then(() => setLoading(false))
              .then(() => setVisible(false))
              .then(() => onProjectChange(projectId))
              .catch(() => setLoading(false));
          } else {
            setLoading(false);
          }
        }}
        onCancel={() => setVisible(false)}
        okButtonProps={{ disabled: submitDisabled }}>
        <Form layout="vertical">
          <Form.Item>
            {Object.keys(filter || {}).map(k => {
              const valueText = `${filter[k]}`;
              return (
                <Tag key={k}>
                  <strong>{`${k}: `}</strong>
                  {valueText.length > 50 ? `${valueText.substring(0, 50)}...` : valueText}
                </Tag>
              );
            })}
          </Form.Item>
          <Form.Item label="Name of Your Search">
            <Input value={portfolioName} onChange={e => setPortfolioName(e.target.value)} />
          </Form.Item>
          <Form.Item>
            <Dropdown
              menu={{
                items: modeItems,
                onClick: ({ key }) => {
                  const m = key;
                  setMode(m);
                  if (m === "new") {
                    setProjectId(null);
                  } else if (m === "existing") {
                    setProjectName("");
                  } else if (m === "bare") {
                    setProjectId(null);
                    setProjectName("");
                  }
                }
              }}
              getPopupContainer={trigger => trigger.parentNode}
            >
              <Button>
                {modeLabel[mode]} <DownOutlined />
              </Button>
            </Dropdown>
          </Form.Item>

          {mode === "new" ? (
            <Form.Item label="Project Name">
              <Input value={projectName} onChange={e => setProjectName(e.target.value)} />
            </Form.Item>
          ) : mode === "existing" ? (
            <Form.Item label="Pick a Project">
              <Select
                value={projectId}
                placeholder="Project"
                onChange={val => setProjectId(val)}
                getPopupContainer={trigger => trigger.parentNode}
              >
                {projects.map(({ id, name }) => (
                  <Select.Option key={id} value={id}>
                    {name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            null
          )}
        </Form>
      </Modal>
    </Fragment>
  );
}

SaveUserQuery.propTypes = {
  filter: PropTypes.object.isRequired
};

export default withRouter(withUser(SaveUserQuery));
